import { SCENARIO_PARTY_POSITION_SOURCE } from '../data/scenarioCommsData';
import { normalizeExerciseVesselApiId, resolveLiveExerciseVesselUuid } from './exerciseVesselRoleLookup';

function rowTimeMs(row) {
  const v = row.timestamp ?? row.time;
  if (v == null) return NaN;
  const ms = new Date(v).getTime();
  return Number.isNaN(ms) ? NaN : ms;
}

/**
 * Resolve [lon, lat] for a scenario comms party at the current story clock.
 * Vessels use last report at or before playbackTimeMs; without playback, last point in load.
 * @param {string} partyName - `from` or `to` label from SCENARIO_COMMS_SCRIPT
 * @param {Array} rawData
 * @param {number|null|undefined} playbackTimeMs
 * @returns {[number, number]|null}
 */
export function getScenarioCommsPartyLonLat(partyName, rawData, playbackTimeMs = null) {
  if (!partyName) return null;
  const src = SCENARIO_PARTY_POSITION_SOURCE[partyName];
  if (!src) return null;

  if (src.kind === 'fixed' && src.lonLat && src.lonLat.length === 2) {
    return [src.lonLat[0], src.lonLat[1]];
  }

  if (src.kind === 'vessel') {
    if (!src.uuid || !rawData || rawData.length === 0) return null;
    const uuid = normalizeExerciseVesselApiId(resolveLiveExerciseVesselUuid(src.uuid) ?? src.uuid);
    if (!uuid) return null;
    const pts = [];
    for (const row of rawData) {
      const id = normalizeExerciseVesselApiId(row.unique_identifier);
      if (!id || id !== uuid) continue;
      const lat = row.lat ?? row.location_latitude;
      const lon = row.lon ?? row.location_longitude;
      if (lat == null || lon == null || Number.isNaN(Number(lat)) || Number.isNaN(Number(lon))) continue;
      const ts = rowTimeMs(row);
      if (Number.isNaN(ts)) continue;
      pts.push({ lon: Number(lon), lat: Number(lat), ts });
    }
    if (pts.length === 0) return null;
    pts.sort((a, b) => a.ts - b.ts);
    let usePts = pts;
    if (playbackTimeMs != null && !Number.isNaN(playbackTimeMs)) {
      usePts = pts.filter((p) => p.ts <= playbackTimeMs);
    }
    if (usePts.length === 0) return null;
    const last = usePts[usePts.length - 1];
    return [last.lon, last.lat];
  }

  return null;
}

/**
 * Best lon/lat for a comms bubble: sender track head, then recipient track head, then scripted fallback.
 */
export function getScenarioCommsMessageLonLat(msg, rawData, playbackTimeMs = null) {
  if (!msg) return null;
  const fromPos = getScenarioCommsPartyLonLat(msg.from, rawData, playbackTimeMs);
  if (fromPos) return fromPos;
  const toPos = getScenarioCommsPartyLonLat(msg.to, rawData, playbackTimeMs);
  if (toPos) return toPos;
  if (msg.mapAnchor && msg.mapAnchor.length === 2) return [msg.mapAnchor[0], msg.mapAnchor[1]];
  return null;
}

/**
 * Sender and receiver positions for drawing a comms link on the map.
 * Falls back to `mapAnchor` for any missing end so a line can still render.
 * @returns {{ fromLL: [number, number], toLL: [number, number] }|null}
 */
export function getScenarioCommsMessageEndpoints(msg, rawData, playbackTimeMs = null) {
  if (!msg) return null;
  const fb = msg.mapAnchor?.length === 2 ? [Number(msg.mapAnchor[0]), Number(msg.mapAnchor[1])] : null;
  let fromLL = getScenarioCommsPartyLonLat(msg.from, rawData, playbackTimeMs) ?? fb;
  let toLL = getScenarioCommsPartyLonLat(msg.to, rawData, playbackTimeMs) ?? fb;
  if (!fromLL && !toLL) return null;
  if (!fromLL) fromLL = toLL;
  if (!toLL) toLL = fromLL;
  return { fromLL, toLL };
}
